/**
 * Boots the F-200 accounts API client.
 *
 * Wires:
 *  - `OpenAPI.BASE` ← `VITE_ACCOUNTS_BASE_URL` (dev default
 *    `http://localhost:8100`, per F-310 technical spec § 6.1)
 *  - `OpenAPI.TOKEN` ← bearer-token resolver (see "Auth resolver" below)
 *  - `OpenAPI.WITH_CREDENTIALS` ← `false` (ADR-006: tokens travel in
 *    the `Authorization` header, NOT cookies — F-200 does not set any)
 *
 * Per ADR-006 the UI talks to F-200 directly for cloud auth/profile/
 * billing endpoints; F-300 only validates JWTs F-200 issued. This
 * client is one of two parallel API clients in `src/api/`; the other
 * is `gateway-client.ts` (T-005).
 *
 * Called from `src/main.tsx` at startup, alongside `initGatewayClient()`.
 */

import { useAuth } from "./auth-store";
import { OpenAPI } from "./accounts";

/**
 * localStorage key the cloud JWT is persisted under. Kept for the
 * `__internal` export below (tests + integration docs reference the
 * constant), but the resolver now sources its value from
 * `useAuth.getState().jwt` — the store writes through to this exact
 * key so any non-store reader stays consistent.
 *
 * The local-only fallback (`mirlai_jwt_local`) is deliberately ignored
 * here: F-200 would reject a locally-issued token and presenting it
 * would only confuse audit logs. That's why this client reads `jwt`
 * directly, NOT `getActiveJwt()` (which would fall back to localJwt).
 */
const CLOUD_JWT_STORAGE_KEY = "mirlai_jwt";

const DEFAULT_DEV_ACCOUNTS_BASE = "http://localhost:8100";

/**
 * Read the current cloud JWT from the auth store. Returns `""` when
 * no cloud token is set — the request layer treats `""` as "skip the
 * `Authorization` header", which is the desired behaviour for the
 * unauthenticated endpoints (signup, login, refresh, verify-email,
 * password reset).
 *
 * Defensive against early-boot calls fired before the store is
 * hydrated (the store starts with `jwt: null`, which resolves to `""`
 * here, which the resolver handles correctly).
 */
function resolveBearerToken(): string {
  try {
    return useAuth.getState().jwt ?? "";
  } catch {
    return "";
  }
}

/**
 * Initialise the accounts API client. Idempotent — safe to call more
 * than once (subsequent calls overwrite the previous config in place).
 *
 * Orchestrator wires this into `src/main.tsx` after Wave A, alongside
 * `initGatewayClient()`.
 */
export function initAccountsClient(): void {
  OpenAPI.BASE = import.meta.env.VITE_ACCOUNTS_BASE_URL ?? DEFAULT_DEV_ACCOUNTS_BASE;
  // Async resolver: read at call time, so logins / refreshes that
  // happen after init are picked up automatically.
  OpenAPI.TOKEN = async (): Promise<string> => resolveBearerToken();
  OpenAPI.WITH_CREDENTIALS = false;
}

/**
 * Exposed for tests so they can poke at the same constants production
 * code uses. Production code should not depend on this — use
 * `AccountsService.*` methods which read `OpenAPI.TOKEN` automatically.
 */
export const __internal = {
  resolveBearerToken,
  DEFAULT_DEV_ACCOUNTS_BASE,
  CLOUD_JWT_STORAGE_KEY,
};
